import useSWR, { mutate } from "swr";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type { SkillCreate, SkillUpdate } from "~/components/assistant/types";

const datasetSkillsKey = (datasetId: string) => [datasetId, "dataset-skills"] as const;

export function invalidateDatasetSkills(datasetId: string) {
  return mutate(datasetSkillsKey(datasetId));
}

export function useDatasetSkills(api: AssistantApi, datasetId: string | undefined) {
  const {
    data: skills = [],
    mutate: mutateSkills,
    isLoading,
    error,
  } = useSWR(datasetId ? datasetSkillsKey(datasetId) : null, ([id]: readonly [string, string]) =>
    api.listSkills(id).then((r) => r.dataset)
  );

  const toggle = async (skillId: string, enabled: boolean) => {
    await mutateSkills(
      (current) => current?.map((s) => (s.id === skillId ? { ...s, enabled } : s)),
      { revalidate: false }
    );
    try {
      await api.updateSkill(skillId, { enabled });
    } catch (e) {
      await mutateSkills(); // revert to server state
      throw e;
    }
  };

  const createSkill = async (data: Omit<SkillCreate, "scope" | "scope_id">) => {
    if (!datasetId) return;
    await api.createSkill({ ...data, scope: "DATASET", scope_id: datasetId });
    await invalidateDatasetSkills(datasetId);
  };

  const updateSkill = async (skillId: string, data: SkillUpdate) => {
    await api.updateSkill(skillId, data);
    if (datasetId) await invalidateDatasetSkills(datasetId);
  };

  const deleteSkill = async (skillId: string) => {
    await api.deleteSkill(skillId);
    if (datasetId) await invalidateDatasetSkills(datasetId);
  };

  return { skills, isLoading, error, toggle, createSkill, updateSkill, deleteSkill };
}
